import { useSyncExternalStore, useRef } from 'react';
import { useEntityStore } from '../entity-store';
import type { SnapshotInfo } from '../entity-store';

export type { SnapshotInfo };

const EMPTY: readonly SnapshotInfo[] = Object.freeze([]);

/**
 * Build a stable sorted snapshot list for a entityId from the store.
 * Returns the same array reference if contents haven't changed (by snapshot id + timestamp).
 */
function buildSnapshot(
  entityId: string,
  prev: readonly SnapshotInfo[],
): readonly SnapshotInfo[] {
  const snapshots = useEntityStore.getState().snapshots;
  const list: SnapshotInfo[] = [];
  for (const snap of snapshots.values()) {
    if (snap.entityId === entityId) list.push(snap);
  }
  if (list.length === 0) return EMPTY;

  list.sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  // Return previous reference if contents are identical (stable reference for React)
  if (
    list.length === prev.length &&
    list.every((s, i) => s.id === prev[i].id && s.name === prev[i].name)
  ) {
    return prev;
  }

  return Object.freeze(list);
}

/**
 * Returns the list of snapshots for a document, sorted newest-first.
 * Reactively updates when entityStore receives snapshot UPSERTED/DELETED events.
 */
export function useDocumentSnapshots(entityId: string): readonly SnapshotInfo[] {
  const cacheRef = useRef<readonly SnapshotInfo[]>(EMPTY);

  const getSnapshot = () => {
    const next = buildSnapshot(entityId, cacheRef.current);
    cacheRef.current = next;
    return next;
  };

  return useSyncExternalStore(
    useEntityStore.subscribe,
    getSnapshot,
    () => EMPTY,
  );
}
